import ReceivedAtDate from '../../../../../../components/ReceivedAtDate';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone/moment-timezone';

import { actions, PLOT_DATA_RESULT } from '../actions';
import { DATE_MONTH_DAY_YEAR, TIME_FORMAT_12_UPPERCASE, PAGE_LIMIT } from '../../../../../../constants';
import Strings from '../../../../../../Strings';
import Table3, { columnHelper } from '../../../../../../containers/Table/Table3';
import { STEPS_REPORT } from '../../../constant';
import StepsGraphHourly from '../../../../../../components/Graphs/StepsGraphHourly';
import StepsGraph from '../../../../../../components/Graphs/StepsGraph';

const RECOMMENDED_STEPS = 10000;

function StepsReport(props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [dataToShow, setDataToShow] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, total: 0, limit: PAGE_LIMIT });
  const pageRequestRef = React.useRef({ offset: 0, limit: PAGE_LIMIT });
  const minDateFromEventsRef = React.useRef(moment());

  const prepareData = React.useCallback(events => {
    if (events && !events.find(p => p.steps !== 0)) return [];
    return events?.map(d => ({
      date: moment(d.start_timestamp * 1000),
      intervalEnd: d.end_timestamp ? moment.unix(d.end_timestamp) : null,
      backend_timestamp: d.backend_timestamp,
      steps: d.steps,
    }));
  }, []);

  const getPlotData = React.useCallback(() => {
    setIsLoading(true);

    const { deviceId, startDate, endDate, dateRangeType, hubId, kitId, patientId } = props;

    const request = {
      deviceId,
      hubId,
      kitId,
      patientId,
      dataType: STEPS_REPORT,
      startDate,
      endDate,
      resolution: dateRangeType === 'Monthly' ? 24 * 60 * 60 : 60 * 60,
      limit: pageRequestRef.current.limit,
      offset: pageRequestRef.current.offset,
    };

    props.getPlotData(request).then(resp => {
      if (resp.type === PLOT_DATA_RESULT) {
        const prepared = prepareData(resp.response.data);
        setDataToShow(prepared);
        minDateFromEventsRef.current = moment(Math.min(...resp.response.data.map(d => d.timestamp)) * 1000);
        setPagination({
          offset: pageRequestRef.current.offset,
          total: resp.response.pagination?.total || prepared.length,
          limit: pageRequestRef.current.limit,
        });
      }
      setIsLoading(false);
    });
  }, [props.deviceId, props.startDate, props.endDate, props.dateRangeType, props.hubId, props.kitId, props.patientId, prepareData]);

  React.useEffect(() => {
    pageRequestRef.current.offset = 0;
    getPlotData();
  }, [props.deviceId, props.startDate, props.endDate]);

  const onPaginationChange = React.useCallback((newPagination) => {
    pageRequestRef.current.offset = newPagination.pageIndex * pageRequestRef.current.limit;
    getPlotData();
  }, [getPlotData]);

  const onPageSizeChange = React.useCallback((newSize) => {
    pageRequestRef.current.offset = 0;
    pageRequestRef.current.limit = newSize;
    getPlotData();
  }, [getPlotData]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('date', {
      header: Strings.date,
      cell: info => (
        <ReceivedAtDate displayReference={info.getValue()} displayFormat={`${DATE_MONTH_DAY_YEAR} ${TIME_FORMAT_12_UPPERCASE}`} measuredAt={info.row.original.intervalEnd} backendTimestamp={info.row.original.backend_timestamp}>
          {info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : ''}
        </ReceivedAtDate>
      ),
      accessorFn: row => row.date,
    }),
    columnHelper.accessor('time', {
      header: Strings.time,
      cell: info => info.getValue() ? moment(info.getValue()).format(TIME_FORMAT_12_UPPERCASE) : '',
      accessorFn: row => row.date,
    }),
    columnHelper.accessor('steps', {
      header: Strings.steps,
    }),
  ], []);

  const getGraph = () => {
    const minDate =
      moment(props.startDate).year() === 1970 ? minDateFromEventsRef.current : moment(props.startDate);
    const maxDate = moment(props.endDate);

    if (dataToShow.length > 0 && props.dateRangeType === 'Monthly') {
      return (
        <div className="hub-device-events-margin-bottom">
          <StepsGraph
            graphDataDaily={dataToShow || []}
            graphDataHourly={[]}
            minDate={minDate}
            maxDate={maxDate}
            stepsRecommended={RECOMMENDED_STEPS}
            hasBorder
          />
        </div>
      );
    }

    if (dataToShow.length > 0 && props.dateRangeType === 'Daily') {
      const sum = dataToShow.reduce((accumulator, object) => accumulator + object.steps, 0);

      return (
        <div className="hub-device-events-margin-bottom">
          <StepsGraphHourly graphData={dataToShow} date={minDate} goodDay={sum >= RECOMMENDED_STEPS} />
        </div>
      );
    }

    return (
      <div className="empty-row-container bottom-margin">
        <div className="empty-row-inner">{Strings.noDataAvailable}</div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {getGraph()}
      <Table3
        name="steps-report"
        isLoading={isLoading}
        data={dataToShow}
        columns={columns}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        onPageSizeChange={onPageSizeChange}
      />
    </React.Fragment>
  );
}

StepsReport.propTypes = {
  deviceId: PropTypes.string.isRequired,
  patientId: PropTypes.string,
  hubId: PropTypes.string,
  kitId: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  getPlotData: PropTypes.func.isRequired,
  dateRangeType: PropTypes.string,
};

StepsReport.defaultProps = {
  dateRangeType: 'Monthly',
};

const mapDispatchToProps = dispatch => ({
  getPlotData: pageRequest => dispatch(actions.getPlotData(pageRequest)),
});

export default connect(null, mapDispatchToProps)(StepsReport);
